<template>
  <q-card
    flat
    bordered
    class="kds-card overflow-hidden"
    :class="{ 'kds-card--compact': compact }"
  >
    <q-inner-loading
      :showing="saving && !feedbackType"
      color="primary"
    />

    <div class="kds-card-stack relative-position">
      <div class="kds-card-main">
        <q-card-section class="q-pb-sm">
          <div class="row items-start q-col-gutter-md">
            <div class="col">
              <div class="text-h6">#{{ sale.id }}</div>
              <div class="text-body2 text-weight-medium q-mt-xs">{{ customerNameDisplay }}</div>
              <div v-if="customerPhoneDisplay" class="text-caption text-grey-7">
                {{ customerPhoneDisplay }}
              </div>
              <div class="text-caption text-grey-7 q-mt-xs">
                {{ relativeTime }}
              </div>
              <div class="text-caption text-grey-6 q-mt-xs">
                {{ absoluteTime }}
              </div>
              <div class="row q-gutter-xs q-mt-sm items-center">
                <q-chip
                  dense
                  :color="statusColor(sale.fulfillment_status)"
                  text-color="white"
                >
                  {{ fulfillmentStatusLabel(sale.fulfillment_status) }}
                </q-chip>
                <q-chip
                  v-if="showFulfillmentMode && fulfillmentModeLabel"
                  dense
                  outline
                  :color="sale.fulfillment_mode === 'ship' ? 'secondary' : 'primary'"
                >
                  {{ fulfillmentModeLabel }}
                </q-chip>
                <q-chip
                  v-if="showGuestBadge && isGuestShopper"
                  dense
                  outline
                  color="grey-7"
                >
                  {{ t('storeOrders.guestCustomer') }}
                </q-chip>
                <q-chip
                  v-if="lineItemCount != null"
                  dense
                  outline
                  color="grey-7"
                >
                  {{ t('storeOrders.lineItemCount', { count: lineItemCount }) }}
                </q-chip>
              </div>
            </div>
          </div>

          <div class="row q-col-gutter-md q-mt-md">
            <div class="col-12 col-sm-6" v-if="sale.fulfillment_status === 'queued'">
              <q-btn
                color="primary"
                unelevated
                icon="play_arrow"
                :label="t('kds.start')"
                size="lg"
                class="full-width kds-action-btn"
                padding="sm lg"
                @click="$emit('advance', 'in_progress')"
              />
            </div>
            <div class="col-12 col-sm-6" v-if="sale.fulfillment_status === 'in_progress' && !enableShipFlow">
              <q-btn
                color="primary"
                unelevated
                icon="restaurant"
                :label="t('kds.ready')"
                size="lg"
                class="full-width kds-action-btn"
                padding="sm lg"
                @click="$emit('advance', 'ready')"
              />
            </div>
            <template v-if="sale.fulfillment_status === 'in_progress' && enableShipFlow">
              <div v-if="sale.fulfillment_mode !== 'ship'" class="col-12 col-sm-6">
                <q-btn
                  color="primary"
                  unelevated
                  icon="restaurant"
                  :label="t('kds.ready')"
                  size="lg"
                  class="full-width kds-action-btn"
                  padding="sm lg"
                  @click="$emit('advance', 'ready')"
                />
              </div>
              <div :class="sale.fulfillment_mode === 'ship' ? 'col-12' : 'col-12 col-sm-6'">
                <q-btn
                  color="secondary"
                  unelevated
                  icon="local_shipping"
                  :label="t('storeOrders.markShipped')"
                  size="lg"
                  class="full-width kds-action-btn"
                  padding="sm lg"
                  @click="$emit('request-ship')"
                />
              </div>
            </template>
            <div class="col-12 col-sm-6" v-if="sale.fulfillment_status === 'shipped'">
              <q-btn
                color="positive"
                unelevated
                icon="check_circle"
                :label="t('kds.complete')"
                size="lg"
                class="full-width kds-action-btn"
                padding="sm lg"
                @click="$emit('advance', 'completed')"
              />
            </div>
            <div class="col-12 col-sm-6" v-if="sale.fulfillment_status === 'ready'">
              <q-btn
                color="positive"
                unelevated
                icon="check_circle"
                :label="t('kds.complete')"
                size="lg"
                class="full-width kds-action-btn"
                padding="sm lg"
                @click="$emit('advance', 'completed')"
              />
            </div>
          </div>

          <div
            v-if="['queued','in_progress','ready','shipped'].includes(sale.fulfillment_status)"
            class="q-mt-md"
          >
            <q-btn
              outline
              color="negative"
              icon="close"
              :label="t('kds.cancelOrder')"
              size="lg"
              class="full-width kds-action-btn kds-action-btn--danger"
              padding="sm lg"
              @click="$emit('request-cancel')"
            />
          </div>
        </q-card-section>

        <q-separator />
        <q-card-section>
          <div class="kds-lines">
            <div
              v-for="(line, lineIdx) in sale.items"
              :key="`${sale.id}-${line.line_item_id ?? lineIdx}`"
              class="q-mb-sm"
            >
              <div
                class="text-h6"
                :class="{ 'text-body1': compact }"
              >
                ×{{ formatLineQty(line) }} {{ line.product?.name || t('kds.itemFallback') }}
              </div>
              <div v-if="(line.modifiers || []).length" class="text-caption text-grey-7 q-ml-sm">
                {{ formatMods(line.modifiers) }}
              </div>
            </div>
          </div>
        </q-card-section>
        <q-card-actions align="right" class="print-hide q-px-md q-pb-md">
          <q-btn
            v-if="showViewDetail"
            flat
            icon="receipt_long"
            color="primary"
            :label="viewDetailLabel"
            @click="$emit('view-detail')"
          />
          <q-btn flat icon="print" color="grey-8" :label="t('kds.printTicket')" @click="$emit('print')" />
        </q-card-actions>
      </div>

      <Transition name="kds-feedback">
        <div
          v-if="feedbackType && !cancelGraceActive"
          class="kds-feedback-overlay column items-center justify-center text-center"
          :class="feedbackClass(feedbackType)"
        >
          <div class="text-h4 text-weight-bold text-white text-uppercase letter-spacing-1">
            {{ feedbackTitle(feedbackType) }}
          </div>
          <div class="text-body2 text-white q-mt-sm" style="opacity: 0.92">
            {{ feedbackSubtitle(feedbackType, sale.id) }}
          </div>
        </div>
      </Transition>

      <Transition name="kds-feedback">
        <div
          v-if="cancelGraceActive"
          class="kds-feedback-overlay kds-feedback--cancel column items-center justify-center text-center q-pa-xl"
        >
          <div class="text-h4 text-weight-bold text-white text-uppercase letter-spacing-1">
            {{ t('kds.overlayCancelled') }}
          </div>
          <div class="text-body2 text-white q-mt-sm q-mb-lg" style="opacity: 0.9">
            {{ t(cancelPendingLineKey, { id: sale.id }) }}
            <span v-if="cancelSecondsLeft > 0">
              {{ t('kds.undoSeparator') }}{{ t('kds.undoInSeconds', { n: cancelSecondsLeft }) }}
            </span>
          </div>
          <q-btn
            unelevated
            color="white"
            text-color="negative"
            :label="t('kds.undo')"
            size="lg"
            class="kds-action-btn"
            padding="sm xl"
            @click="$emit('undo-cancel')"
          />
        </div>
      </Transition>
    </div>
  </q-card>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatQuantityDisplay } from '@/composables/useQuantity'
import { getQueueCustomerName, getQueueCustomerPhone } from '@/composables/useQueueCustomer'

const props = defineProps({
  sale: { type: Object, required: true },
  compact: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  feedbackType: { type: String, default: null },
  cancelGraceActive: { type: Boolean, default: false },
  cancelSecondsLeft: { type: Number, default: 0 },
  relativeTime: { type: String, default: '' },
  absoluteTime: { type: String, default: '' },
  showViewDetail: { type: Boolean, default: false },
  viewDetailLabel: { type: String, default: '' },
  enableShipFlow: { type: Boolean, default: false },
  showGuestBadge: { type: Boolean, default: false },
  showFulfillmentMode: { type: Boolean, default: false },
  cancelPendingLineKey: { type: String, default: 'kds.cancelPendingLine' }
})

defineEmits(['advance', 'request-cancel', 'undo-cancel', 'print', 'view-detail', 'request-ship'])

const { t, te, locale } = useI18n()

const customerNameDisplay = computed(() => {
  void locale.value
  return getQueueCustomerName(props.sale) || t('kds.customerGuest')
})

const customerPhoneDisplay = computed(() => getQueueCustomerPhone(props.sale))

const isGuestShopper = computed(() => !props.sale?.customer_id)

const lineItemCount = computed(() => {
  const items = props.sale?.items
  if (!Array.isArray(items)) return null
  return items.length
})

const fulfillmentModeLabel = computed(() => {
  const mode = props.sale?.fulfillment_mode
  if (mode === 'ship') {
    return t('storeOrders.fulfillmentModeShip')
  }
  if (mode === 'pickup') {
    return t('storeOrders.fulfillmentModePickup')
  }
  return null
})

function formatLineQty (line) {
  return formatQuantityDisplay(line.quantity, {
    allowFractional: line.product?.allow_fractional
  })
}

function fulfillmentStatusLabel (status) {
  void locale.value
  if (!status) return ''
  const path = `kds.fulfillment.${status}`
  return te(path) ? t(path) : status
}

function statusColor (s) {
  if (s === 'queued') return 'primary'
  if (s === 'in_progress') return 'orange'
  if (s === 'ready') return 'positive'
  if (s === 'shipped') return 'teal'
  return 'grey'
}

function feedbackClass (type) {
  if (type === 'started') return 'kds-feedback--started'
  if (type === 'ready') return 'kds-feedback--ready'
  if (type === 'complete') return 'kds-feedback--complete'
  return ''
}

function feedbackTitle (type) {
  void locale.value
  if (type === 'started') return t('kds.feedbackPreparing')
  if (type === 'ready') return t('kds.feedbackReady')
  if (type === 'complete') return t('kds.feedbackCompleted')
  return ''
}

function feedbackSubtitle (type, saleId) {
  void locale.value
  if (type === 'complete') return t('kds.feedbackAllFinished')
  return t('kds.orderNumberLine', { id: saleId })
}

function formatMods (mods) {
  const parts = (Array.isArray(mods) ? mods : []).map((m) => {
    const label = m.name_snapshot || m.name || ''
    const q = Number(m.quantity)
    if (label && Number.isFinite(q) && q > 1) return `${q}× ${label}`
    return label
  }).filter(Boolean)
  return parts.join(', ')
}
</script>

<style scoped lang="scss">
.kds-card {
  border-radius: 16px;
  transition:
    box-shadow 0.35s ease,
    transform 0.35s ease,
    border-color 0.35s ease;
}
.kds-card--compact {
  .kds-action-btn {
    min-height: 48px;
    font-size: 0.95rem;
  }
}
.kds-card-stack {
  min-height: 120px;
}
.kds-feedback-overlay {
  position: absolute;
  inset: 0;
  z-index: 7000;
  border-radius: 15px;
  padding: 24px;
  pointer-events: auto;
}
.kds-feedback--started {
  background: linear-gradient(145deg, #1565c0 0%, #0d47a1 100%);
}
.kds-feedback--ready {
  background: linear-gradient(145deg, #ef6c00 0%, #e65100 100%);
}
.kds-feedback--complete {
  background: linear-gradient(145deg, #2e7d32 0%, #1b5e20 100%);
}
.kds-feedback--cancel {
  min-height: 220px;
  background: linear-gradient(145deg, #c62828 0%, #b71c1c 100%);
}
.kds-action-btn {
  min-height: 52px;
  font-weight: 600;
  border-radius: 12px;
}
.kds-action-btn--danger {
  border-width: 2px;
}
.letter-spacing-1 {
  letter-spacing: 0.08em;
}
.kds-feedback-enter-active,
.kds-feedback-leave-active {
  transition:
    opacity 0.38s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.38s cubic-bezier(0.4, 0, 0.2, 1);
}
.kds-feedback-enter-from,
.kds-feedback-leave-to {
  opacity: 0;
  transform: scale(0.94);
}
.kds-feedback-enter-to,
.kds-feedback-leave-from {
  opacity: 1;
  transform: scale(1);
}
</style>
